import {
  Bell,
  Bookmark,
  Briefcase,
  Calendar,
  ListChecks,
  Lock,
  MessageSquare,
  ShieldCheck,
  UserRound,
  type LucideIcon,
} from "lucide-react";

/** Anchor links shown in the sticky nav (hidden on mobile). */
export const NAV_LINKS = [
  { label: "Roles", href: "#roles" },
  { label: "Features", href: "#features" },
  { label: "Stories", href: "#stories" },
  { label: "FAQ", href: "#faq" },
] as const;

export interface RoleItem {
  key: string;
  title: string;
  blurb: string;
  bullets: string[];
  image: string;
  alt: string;
  portalHref: string;
}

/** The four platform roles, rendered as full-width horizontal cards. */
export const ROLES: RoleItem[] = [
  {
    key: "student",
    title: "Student / Talent",
    blurb:
      "Showcase your work and track every step. A profile synced from ZenCube gives a clear view of where you stand — browse, apply, and follow each application through the pipeline.",
    bullets: [
      "Browse live openings with eligibility indicators",
      "Apply and track status through the pipeline",
      "Chat with HR and manage interview invites",
    ],
    image: "/illustrations/student.png",
    alt: "Student / Talent illustration",
    portalHref: "/login",
  },
  {
    key: "enterprise",
    title: "Enterprise / Recruiter",
    blurb:
      "Post roles and discover talent. Reach ZenCube students directly — no cold outreach, no inboxes to chase. Open a job, get HR approval, and find the right candidates fast.",
    bullets: [
      "Post openings for HR approval, then go live",
      "Search profiles and bookmark talent with notes",
      "Review forwarded candidates and mark outcomes",
    ],
    image: "/illustrations/enterprise.png",
    alt: "Enterprise / Recruiter illustration",
    portalHref: "/login",
  },
  {
    key: "hr",
    title: "HR Staff",
    blurb:
      "Mediate, review, and keep things moving. HR is the trusted layer between students and enterprises — approving jobs, forwarding the right candidates, and moderating every conversation in between.",
    bullets: [
      "Approve job openings and forward applications",
      "Schedule interviews and moderate every chat",
      "Work from dashboards with stale-application alerts",
    ],
    image: "/illustrations/hr.png",
    alt: "HR Staff illustration",
    portalHref: "/login",
  },
  {
    key: "admin",
    title: "Admin",
    blurb:
      "Configure, govern, and audit. System-wide control with a compliance-grade trail — manage users, set the rules, and read the immutable audit log whenever you need to know who did what.",
    bullets: [
      "Manage users, roles, and fine-grained permissions",
      "Tune eligibility rules and system configuration",
      "Read and export the immutable audit log",
    ],
    image: "/illustrations/admin.png",
    alt: "Admin illustration",
    portalHref: "/login",
  },
];

export interface FeatureItem {
  icon: LucideIcon;
  title: string;
  description: string;
}

/** Platform capabilities, rendered as a 3-column card grid. */
export const FEATURES: FeatureItem[] = [
  {
    icon: UserRound,
    title: "ZenCube-synced profiles",
    description:
      "Talent profiles carry projects, skills, and rubric scores straight from ZenCube — no re-entry.",
  },
  {
    icon: Briefcase,
    title: "Openings & HR approval",
    description:
      "Enterprises draft job openings; HR approves them before they go live to students.",
  },
  {
    icon: ListChecks,
    title: "Application pipeline",
    description:
      "A guarded state machine tracks every application with full status history and transitions.",
  },
  {
    icon: MessageSquare,
    title: "HR-mediated chat",
    description: "Real-time messaging between students and enterprises, always routed through HR.",
  },
  {
    icon: Bell,
    title: "Notifications & alerts",
    description:
      "An in-app inbox plus event-driven email, with per-event preferences and read tracking.",
  },
  {
    icon: Calendar,
    title: "Interview calendar",
    description:
      "HR schedules, reschedules, and cancels interviews with RSVP tracking for everyone involved.",
  },
  {
    icon: Bookmark,
    title: "Watchlist & interest",
    description:
      "Recruiters bookmark promising profiles and attach private notes to express interest.",
  },
  {
    icon: ShieldCheck,
    title: "Immutable audit log",
    description:
      "Every HR and admin action is journaled — actor, change, and timestamp — for compliance.",
  },
  {
    icon: Lock,
    title: "Fine-grained RBAC",
    description:
      "Roles are bundles of granular permissions, so each user sees exactly what they should.",
  },
];

export interface StoryItem {
  quote: string;
  initials: string;
  name: string;
  school: string;
  placement: string;
}

/** Illustrative success stories (static content per the wireframe). */
export const STORIES: StoryItem[] = [
  {
    quote:
      "HR guided me through every stage — I went from applying to an offer without ever chasing a recruiter myself.",
    initials: "EW",
    name: "Emma W.",
    school: "State University '25",
    placement: "Software Engineer · TechCorp Inc.",
  },
  {
    quote:
      "My ZenCube projects did the talking. Enterprises could see the work, and the pipeline kept everything moving.",
    initials: "MT",
    name: "Mark T.",
    school: "Tech Institute '25",
    placement: "Data Analyst · DataFlow Systems",
  },
  {
    quote:
      "I always knew exactly where my application stood. No black box, no cold outreach — just clear next steps.",
    initials: "SK",
    name: "Sarah K.",
    school: "Engineering College '24",
    placement: "ML Engineer · AI Innovations",
  },
];

export interface FaqItem {
  question: string;
  answer: string;
}

export const FAQS: FaqItem[] = [
  {
    question: "How does the hiring pipeline work?",
    answer:
      "Every application advances through five stages — Applied, HR Reviewed, Forwarded, Interview, and Selected. Students see their own progress, HR sees everything, and enterprises see the candidates HR forwards to them, so no one is left guessing where things stand.",
  },
  {
    question: "How do students sign in?",
    answer:
      "Students sign in with their existing ZenCube account through single sign-on (SSO). Their profile — projects, skills, and rubric scores — syncs automatically from ZenCube, so there's nothing to re-enter.",
  },
  {
    question: "How does an enterprise get started?",
    answer:
      "Enterprises register, wait for HR approval, then post openings. Once approved, recruiters can search talent, bookmark promising profiles with notes, and review the candidates HR forwards to them.",
  },
  {
    question: "Will students' contact details be shared with companies?",
    answer:
      "No. The model is HR-mediated by design: students and enterprises never exchange contact information directly. HR reviews fit, forwards the right people, and moderates every conversation in between.",
  },
  {
    question: "What can recruiters see about a student?",
    answer:
      "Recruiters browse profiles — projects, skills, and rubric scores — but not private contact details unless contact permission has been explicitly granted through HR.",
  },
  {
    question: "Is activity on the platform auditable?",
    answer:
      "Yes. Every HR and admin action is written to an immutable audit log — actor, change, and timestamp — and all access is governed by fine-grained, role-based permissions.",
  },
];
